import { useParams, useNavigate } from "react-router-dom"
import { useApiQuery } from "@/lib/api/hooks"
import { cn } from "@/lib/utils"

export function UnitPage() {
  const { unitId } = useParams<{ unitId: string }>()
  const navigate = useNavigate()
  const { data, isPending } = useApiQuery(
    "get",
    "/units/{unitId}/topics",
    { params: { path: { unitId: unitId! } } },
    { enabled: !!unitId }
  )

  const topics = data?.topics ?? []

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate("/")}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Back
        </button>
        <h1 className="text-lg font-semibold">Topics</h1>
      </header>

      <main className="px-6 py-8 max-w-2xl mx-auto">
        {isPending ? (
          <div className="flex items-center justify-center py-24">
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        ) : !topics.length ? (
          <div className="flex items-center justify-center py-24">
            <p className="text-sm text-muted-foreground">No topics in this unit.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {(() => {
              let firstLockedShown = false
              return topics.map((topic) => {
                const isFirstLocked = topic.locked && !firstLockedShown
                if (isFirstLocked) firstLockedShown = true

                function handleClick() {
                  if (topic.locked) return
                  if (!topic.lessonComplete) {
                    navigate(`/topic/${topic.id}/lesson`)
                  } else {
                    navigate(`/topic/${topic.id}/review`)
                  }
                }

                return (
                  <div
                    key={topic.id}
                    className={cn(
                      "rounded-lg border p-4 transition",
                      topic.locked
                        ? "opacity-50 cursor-default"
                        : "hover:border-primary/50 cursor-pointer"
                    )}
                    onClick={handleClick}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {topic.locked && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground shrink-0">
                              <rect x="2" y="6" width="10" height="7" rx="1.5" />
                              <path d="M4 6V4a3 3 0 0 1 6 0v2" />
                            </svg>
                          )}
                          <span className="font-medium text-sm truncate">
                            {topic.name}
                          </span>
                        </div>
                        {topic.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {topic.description}
                          </p>
                        )}
                        {isFirstLocked && topic.unmetPrerequisites.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1.5">
                            Complete {topic.unmetPrerequisites.join(", ")} to unlock
                          </p>
                        )}
                      </div>

                      <div className="shrink-0 flex items-center gap-3">
                        {topic.estimatedMinutes && !topic.lessonComplete && (
                          <span className="text-xs text-muted-foreground">
                            ~{topic.estimatedMinutes}m
                          </span>
                        )}
                        {topic.lessonComplete && (
                          <button
                            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                            onClick={(e) => { e.stopPropagation(); navigate(`/topic/${topic.id}/review`) }}
                          >
                            Review
                          </button>
                        )}
                        {topic.lessonPassed ? (
                          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M2 6l3 3 5-5" />
                            </svg>
                          </div>
                        ) : topic.lessonComplete ? (
                          <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                          </div>
                        ) : !topic.locked ? (
                          <div className="w-6 h-6 rounded-full border-2 border-primary/30" />
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })
            })()}

            {/* Unit Test */}
            {data?.hasUnitTest && (
              <div
                className={cn(
                  "rounded-lg border-2 p-4 mt-4 transition",
                  data.unitTestUnlocked
                    ? "border-primary/50 hover:border-primary cursor-pointer"
                    : "border-border opacity-50 cursor-default"
                )}
                onClick={() => {
                  if (data.unitTestUnlocked) {
                    navigate(`/unit/${unitId}/test`)
                  }
                }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary shrink-0">
                        <path d="M8 1l2 3h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z" />
                      </svg>
                      <span className="font-semibold text-sm">Unit Test</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {data.unitTestUnlocked
                        ? "All topics mastered. Take the unit test to earn your badge."
                        : "Master every topic in this unit to unlock the unit test."}
                    </p>
                  </div>
                  {!data.unitTestUnlocked && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground shrink-0">
                      <rect x="2" y="6" width="10" height="7" rx="1.5" />
                      <path d="M4 6V4a3 3 0 0 1 6 0v2" />
                    </svg>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  )
}
